/**
 * The EventEntryModel represents entries made by users to an EventModel.
 */
import Vue from 'vue';
import { v4 as uuid4 } from 'uuid';
import Model, { ModelErrors } from './Model';
import { UploadedFileData } from "./UploadedFile";
import { DateTime as LuxonDatetime } from "luxon";
import UserModel, { UserModelData, create as createUser } from "./UserModel";
import VideoModel, { VideoData, create as createVideo } from "./VideoModel";
import UploadedFileModel, {create as createFile } from "./UploadedFile";
import EventWaiverModel, { EventWaiverData, create as createWaiver } from "./EventWaiverModel";
import CartItemModel, { CartItemData, create as createItem } from "./CartItemModel";

export interface EmbeddableEntryData {
  attachments?: UploadedFileData[];
  errors?: ModelErrors[];
  competitor?: UserModelData;
  videos?: VideoData[];
  waiver?: EventWaiverData;
  order_item?: CartItemData;

  [propName: string]: unknown;
}

export interface EventEntryData {
  id: number;

  reference: string;

  event_id: number;

  division_id: number;

  waiver: number;

  user_id: number;

  salutation: string;
  first_name: string;
  last_name: string;

  dan_id: string;
  gup_id: string;
  rank_id: number;

  instructor_id: number;
  instructor: string;

  email: string;
  mobile: string;

  dob?: string;

  gender?: string;

  hyung?: string;

  attachment_references: string[];

  approved: string;

  SKU: string;

  purchaser_salutation: string;
  purchaser_firstname: string;
  purchaser_lastname: string;
  registrar_role: string;
  competitor_id: number;

  address: string;
  address_2: string;
  zipcode: string;
  city: string;
  state: string;
  purchaser_country: string;

  student_country: string;

  paid: boolean;

  _embedded?: EmbeddableEntryData;

  backup_video_url?: string;

  [propName: string]: unknown;
}

export default class EventEntryModel extends Model<EventEntryData> {
  competitor?: UserModel;

  videos: VideoModel[] = [];

  attachments: UploadedFileModel[] = [];

  waiver?: EventWaiverModel;

  order_item?: CartItemModel;

  constructor(data?: EventEntryData, errors?: ModelErrors) {
    super(data, errors);

    if (!this.data.reference) {
      this.generateReference();
    }

    if (!this.data.attachment_references) {
      this.data.attachment_references = [];
    }

    if (data?._embedded?.competitor) {
      Vue.set(this, 'competitor', createUser(data._embedded.competitor));
    }

    if (data?._embedded?.attachments) {
      this.updateAttachments(data._embedded.attachments);
    }

    if (data?._embedded?.videos) {
      this.updateVideos(data._embedded.videos);
    }

    if (data?._embedded?.waiver) {
      this.updateWaiver(data._embedded.waiver);
    }

    if (data?._embedded?.order_item) {
      this.updateOrderItem(data._embedded.order_item);
    }
  }

  updateWaiver(waiver: EventWaiverData): void {
    Vue.set(this, 'waiver', createWaiver(waiver));
  }

  updateOrderItem(item: CartItemData): void {
    Vue.set(this, 'order_item', createItem(item));
  }

  updateVideos(videosData: VideoData[]): void {
    const videos = [];
    for (const vd of videosData) {
      videos.push(createVideo(vd));
    }

    Vue.set(this, 'videos', videos);
  }

  updateAttachments(attachmentData: UploadedFileData[]): void {
    const attachments = [];
    for (const at of attachmentData) {
      attachments.push(createFile(at));
    }

    Vue.set(this, 'attachments', attachments);
  }

  defaults(): EventEntryData {
    return {
      id: 0,
      reference: '',
      event_id: 0,
      division_id: 0,
      user_id: 0,
      salutation: '',
      first_name: '',
      last_name: '',
      dan_id: '',
      gup_id: '',
      rank_id: 0,
      instructor_id: 0,
      instructor: '',
      email: '',
      mobile: '',
      dob: '',
      gender: 'male',
      hyung: '',
      attachment_references: [],
      approved: '',
      SKU: '',
      purchaser_salutation: '',
      purchaser_firstname: '',
      purchaser_lastname: '',
      purchaser_country: '',
      registrar_role: '',
      competitor_id: 0,
      address: '',
      address_2: '',
      zipcode: '',
      city: '',
      state: '',
      backup_video_url: '',
      waiver: 0,
      paid: false,
      student_country: '',
    };
  }

  private generateReference(): void {
    this.data.reference = uuid4();
  }

  getAge(): number {
    if (this.data.dob) {
      return Math.floor(LuxonDatetime.fromFormat(this.data.dob, 'yyyy-MM-dd').diffNow('years').years * -1);
    }
    return 0;
  }

  isPaid(): boolean {
    return this.data.paid;
  }

  isSaved(): boolean {
    return this.data.id > 0;
  }

  isMissingVideo(): boolean {
    if (this.isSpectator()) {
      return false;
    }

    const videoAttachments = this.attachments.filter(v => v.data.file_type === 'video');
    return videoAttachments.length === 0 && this.videos.length === 0 && this.data.backup_video_url === "";
  }

  hasCompletedVideo(): boolean {
    return this.videos.length > 0 && this.videos[0].isApproved();
  }

  getFullName(): string {
    if (this.isSpectator()) {
      return this.data.purchaser_firstname + ' ' + this.data.purchaser_lastname;
    }
    return this.data.first_name + ' ' + this.data.last_name;
  }

  isUnpaid(): boolean {
    return !this.isPaid();
  }

  isSpectator(): boolean {
    return this.data.registrar_role === 'spectator';
  }

  isWithdrawn(): boolean {
    return this.data.approved === 'withdrawn';
  }
}

export function create(data?: EventEntryData, errors?: ModelErrors): EventEntryModel {
  return new EventEntryModel(data, errors);
}

export class GuardianDataMapper {
  static fromModel(model: EventEntryModel): GuardianDetails {
    return {
      salutation: model.data.purchaser_salutation ?? '',
      first_name: model.data.purchaser_firstname ?? '',
      last_name: model.data.purchaser_lastname ?? '',
    }
  }

  static toModel(details: GuardianDetails, model: EventEntryModel): EventEntryModel {
    Vue.set(model.data, 'purchaser_salutation', details.salutation);
    Vue.set(model.data, 'purchaser_firstname', details.first_name);
    Vue.set(model.data, 'purchaser_lastname', details.last_name);
    return model;
  }

  static errorsFromModel(model: EventEntryModel): GuardianDetails {
    return {
      salutation: model.errors.purchaser_salutation ?? '',
      first_name: model.errors.purchaser_firstname ?? '',
      last_name: model.errors.purchaser_lastname ?? '',
    }
  }

}

export class StudentDataMapper {
  static fromModel(model: EventEntryModel): StudentDetails {
    return {
      salutation: model.data.salutation ?? '',
      first_name: model.data.first_name ?? '',
      last_name: model.data.last_name ?? '',
      dob: model.data.dob ?? '',
      gender: model.data.gender ?? '',
      division_id: '' + (model.data.division_id || ''),
      hyung: model.data.hyung ?? '',
      email: model.data.email ?? '',
      mobile: model.data.mobile ?? '',
      dan_id: model.data.dan_id ?? '',
      rank_id: '' + (model.data.rank_id || ''),
      instructor_id: !model.data.instructor_id ? '' : '' + model.data.instructor_id,
      instructor: model.data.instructor ?? '',
      gup_id: model.data.gup_id ?? '',
      student_country: model.data.student_country ?? '',
    }
  }

  static toModel(details: StudentDetails, model: EventEntryModel): EventEntryModel {
    Vue.set(model.data, 'salutation', details.salutation)
    Vue.set(model.data, 'first_name', details.first_name)
    Vue.set(model.data, 'last_name', details.last_name)
    Vue.set(model.data, 'dob', details.dob)
    Vue.set(model.data, 'gender', details.gender)
    Vue.set(model.data, 'division_id', details.division_id ? parseInt(details.division_id) : 0)
    Vue.set(model.data, 'hyung', details.hyung)
    Vue.set(model.data, 'email', details.email)
    Vue.set(model.data, 'mobile', details.mobile)
    Vue.set(model.data, 'dan_id', details.dan_id)
    Vue.set(model.data, 'gup_id', details.gup_id)
    Vue.set(model.data, 'rank_id', details.rank_id ? parseInt(details.rank_id) : 0)
    Vue.set(model.data, 'instructor_id', details.instructor_id ? parseInt(details.instructor_id) : 0)
    Vue.set(model.data, 'instructor', details.instructor)
    Vue.set(model.data, 'student_country', details.student_country)
    return model;
  }

  static errorsFromModel(model: EventEntryModel): StudentDetails {
    return {
      salutation: model.errors.salutation ?? '',
      first_name: model.errors.first_name ?? '',
      last_name: model.errors.last_name ?? '',
      dob: model.errors.dob ?? '',
      gender: model.errors.gender ?? '',
      division_id: model.errors.division_id ?? '',
      hyung: model.errors.hyung ?? '',
      email: model.errors.email ?? '',
      mobile: model.errors.mobile ?? '',
      dan_id: model.errors.dan_id ?? '',
      rank_id: model.errors.rank_id ?? '',
      gup_id: model.errors.gup_id ?? '',
      instructor_id: model.errors.instructor_id ?? '',
      instructor: model.errors.instructor ?? '',
      student_country: model.errors.student_country ?? '',
    }
  }
}

export class AddressDataMapper {
  static fromModel(model: EventEntryModel): AddressDetails {
    return {
      address: model.data.address ?? '',
      address_2: model.data.address_2 ?? '',
      city: model.data.city ?? '',
      zipcode: model.data.zipcode ?? '',
      state: model.data.state ?? '',
      purchaser_country: model.data.purchaser_country ?? '',
    }
  }

  static toModel(details: AddressDetails, model: EventEntryModel): EventEntryModel {
    Vue.set(model.data, 'address', details.address)
    Vue.set(model.data, 'address_2', details.address_2)
    Vue.set(model.data, 'city', details.city)
    Vue.set(model.data, 'zipcode', details.zipcode)
    Vue.set(model.data, 'state', details.state)
    Vue.set(model.data, 'purchaser_country', details.purchaser_country)
    return model;
  }

  static errorsFromModel(model: EventEntryModel): AddressDetails {
    return {
      address: model.errors.address ?? '',
      address_2: model.errors.address_2 ?? '',
      city: model.errors.city ?? '',
      zipcode: model.errors.zipcode ?? '',
      state: model.errors.state ?? '',
      purchaser_country: model.errors.purchaser_country ?? '',
    }
  }
}
